import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Rocket, Eye, Quote, HelpCircle, ArrowRight, ChevronLeft, ChevronRight, BookOpen, Compass, Play, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { ShareBar } from "../components/ShareBar";
import { AcaPromiseBanner } from "../components/AcaPromiseBanner";
import Seo from "../components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (id, rev) => `${BACKEND}/api/publications/${id}/image${rev ? `?v=${rev}` : ""}`;
const videoUrl = (id) => `${BACKEND}/api/publications/${id}/video`;

export default function BlogPost() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Mode aperçu admin (?preview=1) : charge l'article même non publié via l'endpoint
  // admin (jeton requis — un simple visiteur avec le lien obtient un 404), sans compter de vue.
  const isPreview = searchParams.get("preview") === "1";
  const [post, setPost] = useState(null);
  const [views, setViews] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [playVideo, setPlayVideo] = useState(false); // façade : clic sur la couverture → vidéo
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    setPost(null); setNotFound(false); setPlayVideo(false);
    api.get(isPreview ? `/admin/publications/${id}/preview` : `/public/publications/${id}`)
      .then((r) => {
        setPost(r.data);
        setViews(r.data.views || 0);
        if (!isPreview) api.post(`/public/publications/${id}/view`).then((v) => setViews(v.data.views)).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id, isPreview]);

  if (notFound) {
    return (
      <div className="px-5 pt-36 pb-24 text-center sm:px-8" data-testid="blog-post-notfound">
        <p className="text-ink-secondary">Cette publication est introuvable ou n’est plus disponible.</p>
        <Link to="/blog" className="mt-4 inline-flex items-center gap-1.5 text-sm text-cyan hover:underline"><ArrowLeft size={15} /> Retour au blog</Link>
      </div>
    );
  }

  const graphs = [];
  if (post) {
    const img = post.has_image ? imgUrl(id, post.image_rev) : "https://appchallengeacademy.com/app-icon.png";
    if (post.kind === "video") {
      graphs.push({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: post.title,
        description: post.excerpt || "",
        thumbnailUrl: [img],
        uploadDate: post.published_at || undefined,
        contentUrl: videoUrl(id),
        embedUrl: `https://appchallengeacademy.com/blog/${id}`,
        duration: "PT12S",
        publisher: {
          "@type": "Organization",
          name: "APP CHALLENGE ACADEMY",
          logo: { "@type": "ImageObject", url: "https://appchallengeacademy.com/app-icon.png" },
        },
      });
    } else {
      const fullText = [post.body, ...(post.sections || []).map((s) => `${s.heading}. ${s.content}`)].join(" ");
      graphs.push({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt || "",
        image: [img],
        articleBody: fullText || undefined,
        wordCount: fullText ? fullText.split(/\s+/).length : undefined,
        articleSection: (post.sections || []).map((s) => s.heading).filter(Boolean),
        datePublished: post.published_at || undefined,
        dateModified: post.published_at || undefined,
        inLanguage: "fr-FR",
        isPartOf: { "@type": "CreativeWorkSeries", name: "L'aventure App Challenge Academy" },
        keywords: (post.hashtags || []).map((h) => h.replace(/^#/, "")).join(", ") || undefined,
        mainEntityOfPage: { "@type": "WebPage", "@id": `https://appchallengeacademy.com/blog/${id}` },
        author: { "@type": "Organization", name: "APP CHALLENGE ACADEMY" },
        publisher: {
          "@type": "Organization",
          name: "APP CHALLENGE ACADEMY",
          logo: { "@type": "ImageObject", url: "https://appchallengeacademy.com/app-icon.png" },
        },
      });
    }
    if (post.faq && post.faq.length) {
      graphs.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((x) => ({
          "@type": "Question",
          name: x.q,
          acceptedAnswer: { "@type": "Answer", text: x.a },
        })),
      });
    }
  }

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={post?.title || "Publication"}
        description={post?.excerpt || ""}
        path={`/blog/${id}`}
        type="article"
        image={post?.has_image ? imgUrl(id, post?.image_rev) : undefined}
        publishedTime={post?.published_at}
        keywords={(post?.hashtags || []).map((h) => h.replace(/^#/, "")).join(", ")}
        jsonLd={graphs}
      />
      <article className="mx-auto max-w-3xl px-5 pt-32 pb-24 sm:px-8">
        <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 rounded-full border border-cyan/40 bg-cyan/10 px-5 py-2.5 text-sm font-600 uppercase tracking-wide text-cyan transition-all hover:-translate-x-0.5 hover:border-cyan hover:bg-cyan/20 hover:glow-cyan sm:text-base">
          <ArrowLeft size={18} /> Retour au blog
        </Link>

        {isPreview && post && (
          <div className="mt-5 flex items-center gap-2.5 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm font-600 text-amber-300" data-testid="blog-post-preview-banner">
            <ShieldCheck size={16} className="shrink-0" />
            Aperçu administrateur — cette publication n’est pas visible par les visiteurs{post.status !== "published" ? (post.scheduled_at ? ` (programmée le ${new Date(post.scheduled_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })})` : " (en préparation)") : ""}. Les vues ne sont pas comptées.
          </div>
        )}

        {post && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-cyan" data-testid="blog-post-episode-badge">
              <Rocket size={12} /> {post.kind !== "video" && post.episode_number ? `Épisode ${post.episode_number} · L'aventure ACA` : "APP CHALLENGE ACADEMY"}
            </div>
            <h1 className="mt-4 font-display text-3xl font-900 leading-tight tracking-tight text-white sm:text-4xl">{post.title}</h1>
            {post.excerpt && <p className="mt-4 text-lg text-ink-secondary">{post.excerpt}</p>}
            {post.published_at && (
              <div className="mt-3 flex items-center gap-4 text-xs text-ink-tertiary">
                <span>Publié le {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-1.5" data-testid="blog-post-views"><Eye size={13} className="text-cyan" /> {views} vue{views > 1 ? "s" : ""}</span>
              </div>
            )}

            {(post.kind === "video" || post.has_video) ? (
              post.has_video ? (
                <div className={`mt-8 overflow-hidden rounded-2xl border border-white/10 bg-black ${post.vertical ? "mx-auto max-w-[380px]" : ""}`} data-testid="blog-post-video">
                  {!playVideo && post.has_image ? (
                    /* Façade : l'image de couverture s'affiche d'abord, sur tous les
                       navigateurs. Un clic dessus démarre la vidéo (avec le son,
                       puisque le clic est un geste de l'utilisateur). */
                    <button type="button" onClick={() => setPlayVideo(true)} data-testid="blog-post-video-facade"
                      className="group relative block w-full cursor-pointer">
                      <img src={imgUrl(id, post.image_rev)} alt={post.title} className="w-full transition-transform duration-500 group-hover:scale-[1.02]" />
                      <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/30 transition-colors group-hover:bg-black/45">
                        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-cyan text-void shadow-[0_0_40px_rgba(0,240,255,0.5)] transition-transform group-hover:scale-110">
                          <Play size={34} className="ml-1" fill="currentColor" />
                        </span>
                        <span className="text-sm font-600 uppercase tracking-wider text-white">Lire la vidéo</span>
                      </span>
                    </button>
                  ) : (
                    <>
                      <video src={videoUrl(id)} poster={post.has_image ? imgUrl(id, post.image_rev) : undefined}
                        ref={(el) => { if (el) el.play().catch(() => {}); }}
                        autoPlay={!playVideo ? true : undefined} muted={!playVideo} loop playsInline controls className="w-full" data-testid="blog-post-video-player" />
                      <p className="bg-surface px-4 py-2 text-[11px] text-ink-tertiary">
                        La vidéo tourne en boucle — réglages du son et de la lecture via les commandes.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="mt-8 rounded-2xl border border-cyan/20 bg-cyan/5 p-10 text-center text-sm text-ink-secondary" data-testid="blog-post-video-pending">
                  La vidéo est en cours de génération. Revenez dans quelques minutes.
                </div>
              )
            ) : post.has_image && (
              <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
                <img src={imgUrl(id, post.image_rev)} alt={post.title} className="w-full object-cover" />
              </div>
            )}

            <AcaPromiseBanner className="mt-8" />

            <div className="prose-invert mt-8 space-y-4 text-base leading-relaxed text-ink-secondary" data-testid="blog-post-body">
              {(post.body || "").split("\n").filter((l) => l.trim()).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {post.sections && post.sections.length > 0 && (
              <div className="mt-8 space-y-8" data-testid="blog-post-sections">
                {post.sections.map((s, i) => (
                  <section key={i} data-testid={`blog-post-section-${i}`}>
                    <h2 className="font-display text-xl font-700 leading-snug text-white sm:text-2xl">{s.heading}</h2>
                    <div className="mt-3 space-y-3 text-base leading-relaxed text-ink-secondary">
                      {(s.content || "").split("\n").filter((l) => l.trim()).map((para, j) => (
                        <p key={j}>{para}</p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {post.faq && post.faq.length > 0 && (
              <div className="mt-10" data-testid="blog-post-faq">
                <h2 className="font-display text-xl font-700 text-white">Questions fréquentes</h2>
                <div className="mt-4 space-y-3">
                  {post.faq.map((qa, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-surface p-5">
                      <div className="flex items-start gap-2 font-display text-base font-600 text-white">
                        <HelpCircle size={17} className="mt-0.5 shrink-0 text-cyan" /> {qa.q}
                      </div>
                      <p className="mt-2 pl-6 text-sm leading-relaxed text-ink-secondary">{qa.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {post.testimonial && post.testimonial.quote && (
              <figure className="mt-10 rounded-2xl border border-cyan/20 bg-cyan/5 p-6" data-testid="blog-post-testimonial">
                <Quote size={22} className="text-cyan" />
                <blockquote className="mt-3 font-display text-lg font-500 italic leading-relaxed text-white">« {post.testimonial.quote} »</blockquote>
                <figcaption className="mt-3 text-sm text-ink-secondary">
                  <span className="font-600 text-white">{post.testimonial.author}</span>
                  {post.testimonial.role ? ` — ${post.testimonial.role}` : ""}
                </figcaption>
              </figure>
            )}

            {((post.hashtags && post.hashtags.length > 0) || (post.mentions && post.mentions.length > 0)) && (
              <div className="mt-8" data-testid="blog-post-tags">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Mots-clés &amp; mentions de l’article</div>
                {post.hashtags && post.hashtags.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-2" data-testid="blog-post-hashtags">
                    {post.hashtags.map((h, i) => (
                      <span key={i} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-cyan">{h}</span>
                    ))}
                  </div>
                )}
                {post.mentions && post.mentions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="blog-post-mentions">
                    {post.mentions.map((m, i) => (
                      <span key={i} className="rounded-full border border-cyan/25 bg-cyan/[0.06] px-3 py-1 text-xs font-600 text-cyan">{m}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {post.kind !== "video" && (post.teaser_next || post.next_episode || post.prev_episode) && (
              <div className="mt-12" data-testid="blog-post-story-nav">
                {post.teaser_next && (
                  <div className="rounded-2xl border border-cyan/30 bg-gradient-to-br from-cyan/10 to-transparent p-6" data-testid="blog-post-teaser-next">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cyan"><BookOpen size={13} /> À suivre dans le prochain épisode</div>
                    <p className="mt-3 font-display text-lg font-500 italic leading-relaxed text-white">{post.teaser_next}</p>
                    {post.next_episode && (
                      <Link to={`/blog/${post.next_episode.id}`} data-testid="blog-post-next-link" className="mt-4 inline-flex items-center gap-2 text-sm font-600 text-cyan transition-colors hover:text-white">
                        Lire l’épisode {post.next_episode.episode_number} <ArrowRight size={15} />
                      </Link>
                    )}
                  </div>
                )}
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {post.prev_episode && (
                    <Link to={`/blog/${post.prev_episode.id}`} data-testid="blog-post-prev-episode" className="group rounded-xl border border-white/10 bg-surface p-4 transition-colors hover:border-cyan/40">
                      <div className="flex items-center gap-1.5 text-xs text-ink-tertiary"><ChevronLeft size={14} /> Épisode précédent</div>
                      <div className="mt-1 font-display text-sm font-600 text-white transition-colors group-hover:text-cyan">{post.prev_episode.title}</div>
                    </Link>
                  )}
                  {post.next_episode && (
                    <Link to={`/blog/${post.next_episode.id}`} data-testid="blog-post-next-episode" className="group rounded-xl border border-white/10 bg-surface p-4 text-right transition-colors hover:border-cyan/40 sm:col-start-2">
                      <div className="flex items-center justify-end gap-1.5 text-xs text-ink-tertiary">Épisode suivant <ChevronRight size={14} /></div>
                      <div className="mt-1 font-display text-sm font-600 text-white transition-colors group-hover:text-cyan">{post.next_episode.title}</div>
                    </Link>
                  )}
                </div>
              </div>
            )}

            {post.related && post.related.length > 0 && (
              <div className="mt-10" data-testid="blog-post-related">
                <h2 className="font-display text-xl font-700 text-white">À lire aussi</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {post.related.map((r) => (
                    <Link key={r.id} to={`/blog/${r.id}`} data-testid={`blog-related-${r.id}`} className="group rounded-xl border border-white/10 bg-surface p-4 transition-colors hover:border-cyan/40">
                      {r.episode_number && <div className="text-[11px] uppercase tracking-wide text-cyan">Épisode {r.episode_number}</div>}
                      <div className="mt-1 font-display text-sm font-600 leading-snug text-white transition-colors group-hover:text-cyan">{r.title}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 rounded-2xl border border-white/10 bg-surface p-6" data-testid="blog-post-internal-links">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cyan"><Compass size={13} /> Poursuivez l’aventure</div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <Link to="/comment-ca-marche" data-testid="blog-link-how" className="text-sm text-ink-secondary transition-colors hover:text-cyan">→ Comment fonctionne l’Academy</Link>
                <Link to="/inscription" data-testid="blog-link-register" className="text-sm text-ink-secondary transition-colors hover:text-cyan">→ Déposer gratuitement mon idée</Link>
                <Link to="/challenge" data-testid="blog-link-challenge" className="text-sm text-ink-secondary transition-colors hover:text-cyan">→ Découvrir le challenge</Link>
                <Link to="/blog" data-testid="blog-link-blog" className="text-sm text-ink-secondary transition-colors hover:text-cyan">→ Tous les épisodes du blog</Link>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-cyan/30 bg-cyan/10 p-6 text-center" data-testid="blog-post-cta-idea">
              <div className="font-display text-xl font-700 text-white">Vous avez une idée d’application ?</div>
              <p className="mt-2 text-sm text-ink-secondary">Inscrivez-la gratuitement — l’Academy peut la sélectionner et la développer.</p>
              <Link to="/inscription" data-testid="blog-cta-register" className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan px-7 py-3 text-sm font-700 uppercase tracking-wide text-void transition-all hover:glow-cyan">
                Inscrivez votre idée <ArrowRight size={16} />
              </Link>
            </div>

            <div className="mt-10 rounded-2xl border border-cyan/30 bg-gradient-to-br from-cyan/10 to-transparent p-6 text-center" data-testid="blog-post-share-cta">
              <div className="font-display text-xl font-700 text-white">Cet article vous a plu ? Partagez-le sur vos réseaux</div>
              <p className="mt-2 text-sm text-ink-secondary">Un partage aide d’autres porteurs d’idées à découvrir l’Academy — merci pour eux.</p>
              <div className="mt-4 flex justify-center">
                <ShareBar url={`${origin}/blog/${id}`} title={post.title} publicationId={id} hashtags={post.hashtags} compact />
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-cyan/20 bg-cyan/5 p-6 text-center">
              <div className="font-display text-lg font-700 text-white">Suivez l’aventure jusqu’à la rentrée</div>
              <p className="mt-2 text-sm text-ink-secondary">Rentrée de App Challenge Academy (ACA) le 09 septembre 2026.</p>
              <Link to="/blog" className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan px-6 py-3 text-sm font-600 text-void transition-all hover:glow-cyan">
                S’abonner au blog
              </Link>
            </div>

            {/* Bas de signature ACA */}
            <footer className="mt-12 border-t border-white/10 pt-8 text-center" data-testid="blog-post-signature">
              <div className="font-display text-sm font-700 uppercase tracking-[0.25em] text-cyan">APP CHALLENGE ACADEMY</div>
              <p className="mx-auto mt-3 max-w-md text-sm italic text-ink-secondary">
                « Vous avez l’idée. Nous la rendons réelle. » — Proposez gratuitement votre idée d’application, l’Academy s’occupe du reste.
              </p>
              <Link to="/inscription" data-testid="blog-signature-deposit"
                className="mt-5 inline-flex items-center gap-2 text-sm font-600 text-cyan transition-colors hover:text-white">
                Déposer votre idée <ArrowRight size={15} />
              </Link>
            </footer>
          </motion.div>
        )}
      </article>
    </div>
  );
}
